// src/components/HeroSection.styles.ts
// Home page hero.
//
// Shares the About page's visual language — same 90%/1120px container, same
// section rhythm, same type scale, same hairline borders and square marks — so
// the two pages read as one site rather than as separate templates.
//
// Styles live in their own module to match components/about/*.styles.ts.
import styled from 'styled-components';
import { motion } from 'framer-motion';

import Button from './Button';
import { device, minDevice } from '../styles/breakpoints';

const sansStack = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

export const HeroRoot = styled.section`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.heroBackground};
  color: ${({ theme }) => theme.text};
  padding: ${({ theme }) => theme.spacing['2xl']} 0 ${({ theme }) => theme.spacing['3xl']};

  @media ${minDevice.laptop} {
    /* The navbar is sticky and roughly 62px tall. Subtracting it keeps the
       hero inside the first viewport instead of always overflowing it. */
    min-height: calc(100vh - 62px);
  }
`;

export const HeroContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing['2xl']};
  align-items: center;

  @media ${minDevice.laptop} {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    gap: 4rem;
  }
`;

/* ---------- Identity column ---------- */

export const HeroMain = styled(motion.header)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  min-width: 0;
`;

export const TitleBlock = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
`;

export const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin: 0;
  font-family: ${sansStack};
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const EyebrowMark = styled.span`
  width: 6px;
  height: 6px;
  flex: 0 0 6px;
  background: ${({ theme }) => theme.accent};
`;

export const Name = styled.h1`
  margin: 0;
  font-family: ${sansStack};
  font-size: 2.9rem;
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: ${({ theme }) => theme.text};

  @media ${device.tablet} {
    font-size: 2.4rem;
  }

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

export const Role = styled.p`
  margin: 0;
  max-width: 34ch;
  font-family: ${sansStack};
  font-size: 1.15rem;
  line-height: 1.5;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};

  @media ${device.mobile} {
    font-size: 1.02rem;
  }
`;

export const Supporting = styled(motion.p)`
  margin: 0;
  max-width: 58ch;
  font-family: ${sansStack};
  font-size: 1.02rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* ---------- Experience proof ---------- */

export const ExperienceNote = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: 58ch;
  padding: 0.1rem 0 0.1rem 1.15rem;
  border-left: 2px solid ${({ theme }) => theme.accent};
`;

export const ExperienceValue = styled.span`
  font-family: ${sansStack};
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.text};
`;

export const ExperienceLabel = styled.span`
  font-family: ${sansStack};
  font-size: 0.95rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* ---------- Capability line ---------- */

export const CapabilityList = styled(motion.ul)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.15rem 0;
  margin: 0;
  padding: 0;
  list-style: none;
  font-family: ${sansStack};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const CapabilityItem = styled.li`
  display: inline-flex;
  align-items: center;
  font-weight: 500;

  /* The separator belongs to the item's own box, so it can never be orphaned
     at the end of a wrapped line. */
  &::after {
    content: '•';
    margin: 0 0.7rem;
    color: ${({ theme }) => theme.accent};
  }

  &:last-child::after {
    content: none;
  }
`;

/* ---------- Calls to action ---------- */

export const CtaRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.9rem;
`;

export const PrimaryCta = styled(Button)`
  button {
    background: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.background};
    border-color: ${({ theme }) => theme.accent};
    font-weight: 600;
  }

  button:hover {
    background: ${({ theme }) => theme.text};
    color: ${({ theme }) => theme.background};
    border-color: ${({ theme }) => theme.text};
  }
`;

/* TertiaryCta - a plain underlined link-style control - was removed with the
   hero's second and third CTAs. The hero now carries one primary action, so
   there is no tertiary tier to style. */

/* ---------- System layer diagram ---------- */

export const LayersPanel = styled(motion.aside)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  min-width: 0;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const LayersLabel = styled.p`
  margin: 0;
  font-family: ${monoStack};
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const LayerList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

/* Row spacing lives on the children rather than the row, so the marker column
   stretches through the gap and its connector reaches the next marker. */
export const LayerRow = styled.li`
  display: flex;
  gap: 0.85rem;

  &:last-child > * {
    padding-bottom: 0;
  }
`;

export const MarkerCol = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 6px;
  padding-top: 0.42rem;
  padding-bottom: 1.1rem;
`;

export const LayerMarker = styled.span`
  width: 6px;
  height: 6px;
  flex: 0 0 6px;
  background: ${({ theme }) => theme.accent};
`;

export const LayerConnector = styled(motion.span)`
  flex: 1;
  min-height: 0.9rem;
  margin-top: 0.45rem;
  width: 1px;
  background: ${({ theme }) => theme.borderColor};
  transform-origin: top center;
`;

export const LayerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  padding-bottom: 1.1rem;
`;

export const LayerName = styled.span`
  font-family: ${sansStack};
  font-size: 0.98rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

export const LayerDetail = styled.span`
  font-family: ${sansStack};
  font-size: 0.88rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.secondary};
`;
